import type { ResolvedRoutesConfig, RoutesConfig } from "single-spa-layout/dist/types/isomorphic/constructRoutes";
import { constructApplications, constructLayoutEngine, constructRoutes, WithLoadFunction } from "single-spa-layout";
import { registerApplication, RegisterApplicationConfig, start } from "single-spa";
import { getAllowedMicroFrontends } from "@newsenec/micro-frontend-manager";
import { filterRoutes } from "./filter-routes";
import { currentMarket } from "@newsenec/language-provider";
import { fetchAllMetadata } from "@newsenec-lib/micro-frontend-utils";

export const startSPA = async (layout: RoutesConfig, filter: boolean = true) => {
    const routes = filter ? await constructAllowedRoutes(layout) : await constructRoutesUnfiltered(layout);
    const applications = findApplications(routes);
    const layoutEngine = constructLayoutEngine({
        routes,
        applications,
        active: false, // activate manually after initializing application micro frontends
    });
    await setupApplications(applications);
    logMetadata();
    layoutEngine.activate();
    start();
};

/**
 * Constructs and filters routes for SingleSPA according to allowed micro frontends.
 */
async function constructAllowedRoutes(layout: RoutesConfig): Promise<ResolvedRoutesConfig> {
    const routes = constructRoutes(layout, { props: {}, loaders: {} });
    const market = currentMarket();
    const admissibleMicroFrontends = await getAllowedMicroFrontends(market);
    filterRoutes(routes, admissibleMicroFrontends);
    return routes;
}

async function constructRoutesUnfiltered(layout: RoutesConfig): Promise<ResolvedRoutesConfig> {
    return constructRoutes(layout, { props: {}, loaders: {} });
}

const compareByName = (thisNamed: { name: string }, thatNamed: { name: string }) => {
    if (thisNamed.name < thatNamed.name) {
        return -1;
    } else if (thisNamed.name > thatNamed.name) {
        return 1;
    } else {
        return 0;
    }
};

const logMetadata = () => {
    fetchAllMetadata()
        .then((metadata) => console.table([...metadata].sort(compareByName)))
        .catch(() => {});
};

const setupApplications = async (applications: (RegisterApplicationConfig & WithLoadFunction)[]): Promise<void> => {
    applications.forEach(registerApplication);
};

const findApplications = (routes: ResolvedRoutesConfig) =>
    constructApplications({
        routes,
        loadApp: ({ name }) =>
            System.import(name)
                .then((module) => {
                    console.info(`Application module '${name}' loaded`);
                    return module;
                })
                .catch((error) => {
                    console.error(`Error loading application module '${name}':`, error);
                    return error;
                }),
    });
